import { MouseEvent, useState } from 'react';

import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { CDS_ZINDEX, Flex } from '@revenuemarket/cds';
import { colors, colorTokens, overlayColors } from '@revenuemarket/design';
import { ResponseStatus } from '@revenuemarket/http';
import { Toast } from '@revenuemarket/ui';
import { Button, Form, Input, List, Modal, ModalProps, Typography } from 'antd';

import {
  useCreateCustomLabel,
  useGroupLabels,
  useDeleteCustomLabel,
  useUpdateCustomLabel,
} from '~hooks/queries/custom-labels/query';
import { useGetCashFlowSettings } from '~hooks/queries/useGetCashFlow';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { useUserId } from '~hooks/useUserId';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { CustomLabel } from '~models/custom-labels';
import { mixpanel } from '~pages/_app';

interface Props extends ModalProps {}

const CustomLabelSettingModal: React.FC<Props> = (props) => {
  const [activeItem, setActiveItem] = useState<CustomLabel.Item | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [editDescription, setEditDescription] = useState<string>('');
  const { companyId } = useDemoAwareCompanyId();
  const userId = useUserId();
  const { data: customLabelData } = useGroupLabels({ companyId });
  const { refetch: refetchCashFlowSetting } = useGetCashFlowSettings(
    companyId as string,
    userId as string,
  );

  const customLabels = customLabelData?.Data?.data ?? [];

  const { mutateAsync: updateCustomLabel } = useUpdateCustomLabel();
  const { mutateAsync: deleteCustomLabel } = useDeleteCustomLabel();
  const { mutateAsync: addCustomLabel } = useCreateCustomLabel();

  const handleAdd = async () => {
    const response = await addCustomLabel({
      companyId: companyId as string,
      name: editValue,
      description: editDescription,
    });
    if (response.Status === ResponseStatus.SUCCESS) {
      mixpanel.track(MIXPANEL_EVENT.LABEL.CREATE_GROUP_LABEL);
      Toast.success('그룹 분류가 추가되었습니다.');
      refetchCashFlowSetting();
      // @ts-ignore
      props.onCancel?.();
    } else {
      Toast.error('그룹 분류 추가에 실패했습니다.');
    }

    reset();
  };

  const handleEdit = async () => {
    if (!activeItem) return;

    const response = await updateCustomLabel({
      companyId: companyId as string,
      name: editValue,
      description: editDescription,
      id: activeItem.id,
    });
    if (response.Status === ResponseStatus.SUCCESS) {
      Toast.success('그룹 분류가 수정되었습니다.');
      refetchCashFlowSetting();
    } else {
      Toast.error('그룹 분류 수정에 실패했습니다.');
    }

    reset();
  };

  const reset = () => {
    setEditValue('');
    setEditDescription('');
    setActiveItem(null);
  };

  const handleClose = (e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => {
    props?.onCancel?.(e);
    reset();
  };

  return (
    <Modal {...props} onCancel={(e) => handleClose(e)} footer={null}>
      <Typography.Text
        style={{
          color: colorTokens.textDescription,
          marginBottom: 16,
        }}
      >
        우리 회사에 맞게 사업부, 지점, 브랜드, 프로젝트 그룹 분류를 만들 수 있어요.
      </Typography.Text>
      <Flex
        gap={8}
        style={{
          width: '100%',
          margin: '16px 0px',
        }}
      >
        <Form labelCol={{ span: 2 }} style={{ width: '100%' }}>
          <Form.Item label="이름" required>
            <Input
              value={editValue}
              placeholder="새 그룹 분류 이름을 입력하세요"
              onChange={(e) => {
                setEditValue(e.target.value);
              }}
            />
          </Form.Item>
          <Form.Item label="설명">
            <Input.TextArea
              value={editDescription}
              rows={2}
              placeholder="새 그룹 분류 설명을 입력하세요"
              onChange={(e) => {
                setEditDescription(e.target.value);
              }}
            />
          </Form.Item>
          <Flex gap={8} justify="flex-end">
            {activeItem ? (
              <Button
                onClick={() => {
                  setActiveItem(null);
                  setEditValue('');
                }}
              >
                취소
              </Button>
            ) : (
              <div />
            )}
            <Button
              type="primary"
              onClick={() => {
                if (!editValue) {
                  Toast.error('그룹 분류를 입력해주세요.');
                  return;
                }

                activeItem ? handleEdit() : handleAdd();
              }}
            >
              {activeItem ? '수정' : '추가'}
            </Button>
          </Flex>
        </Form>
      </Flex>
      <List>
        {customLabels.map((item) => (
          <List.Item
            key={item.id}
            style={{
              display: 'flex',
              justifyContent: 'space-between',

              backgroundColor:
                item.id === activeItem?.id ? overlayColors.primary[2] : colors.grayscaleWhite,
              margin: '0px -16px',
              padding: '8px 16px',
            }}
            onClick={() => {
              setEditValue(item.name);
              setEditDescription(item.description);
              setActiveItem(item);
            }}
          >
            <List.Item.Meta title={item.name} description={item.description} />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',

                gap: 8,
              }}
            >
              <EditOutlined
                style={{
                  color: colorTokens.tooltipIcon,
                }}
              />
              <Button
                style={{
                  color: 'black',
                  display: 'flex',
                  alignItems: 'center',
                  padding: 5,
                }}
                type="link"
                onClick={(e) => {
                  e.stopPropagation();
                  Modal.confirm({
                    zIndex: CDS_ZINDEX.DRAWER.BASE + 10000,
                    title: `${item.name} 그룹 분류를 삭제하시겠습니까?`,
                    content:
                      '분류된 거래내역들의 해당 그룹 분류가 모두 삭제되며, 삭제 후에는 되돌릴 수 없습니다.',
                    okText: '삭제',
                    cancelText: '취소',
                    onOk: async () => {
                      const response = await deleteCustomLabel({
                        companyId: companyId as string,
                        id: item.id,
                      });
                      if (response.Status === ResponseStatus.SUCCESS) {
                        Toast.success('그룹 분류가 삭제되었습니다.');
                      } else {
                        Toast.error('그룹 분류 삭제에 실패했습니다.');
                      }
                    },
                  });
                }}
              >
                <DeleteOutlined
                  height={12}
                  style={{
                    color: colorTokens.tooltipIcon,
                  }}
                />
              </Button>
            </div>
          </List.Item>
        ))}
      </List>
    </Modal>
  );
};

export default CustomLabelSettingModal;
